import { useMutation, useQuery } from '@apollo/client';
import { Button } from '@meshhub/design-tokens/ui';
import { CornerDownLeft, Pencil, Plus } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import {
  ActiveBadge,
  ConfirmDialog,
  FormDialog,
  LoadingOrEmpty,
  MutationError,
  SelectField,
  SettingsPage,
  ShowInactiveToggle,
  SystemLock,
  TextField,
} from '../../components/settings/SettingsKit.web.jsx';
import {
  ADMIN_LOOKUPS_QUERY,
  CREATE_LOOKUP,
  SET_LOOKUP_ACTIVE,
  UPDATE_LOOKUP,
} from '../../lib/queries.js';
import { S } from '../../lib/strings.js';

const KIND = 'SPECIALTY';

/** Build parent → children, preserving sort order. */
function buildTree(rows) {
  const byParent = new Map();
  for (const row of rows) {
    const key = row.parentId ?? '__root__';
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(row);
  }
  for (const list of byParent.values()) list.sort((a, b) => a.sortOrder - b.sortOrder);
  return byParent;
}

/** Descendants of `id`, so the "move to" picker cannot offer a cycle. */
function descendantIds(byParent, id, acc = new Set()) {
  for (const child of byParent.get(id) ?? []) {
    acc.add(child.id);
    descendantIds(byParent, child.id, acc);
  }
  return acc;
}

export function SpecialtiesScreen() {
  const [includeInactive, setIncludeInactive] = useState(true);
  const [editing, setEditing] = useState(null); // {mode:'new'|'edit'|'child', node}
  const [confirming, setConfirming] = useState(null);

  const variables = { kind: KIND, includeInactive };
  const { data, loading } = useQuery(ADMIN_LOOKUPS_QUERY, { variables });
  const refetch = [{ query: ADMIN_LOOKUPS_QUERY, variables }];

  const [createLookup, createState] = useMutation(CREATE_LOOKUP, { refetchQueries: refetch });
  const [updateLookup, updateState] = useMutation(UPDATE_LOOKUP, { refetchQueries: refetch });
  const [setActive, activeState] = useMutation(SET_LOOKUP_ACTIVE, { refetchQueries: refetch });

  const rows = data?.lookups ?? [];
  const byParent = useMemo(() => buildTree(rows), [rows]);
  const roots = byParent.get('__root__') ?? [];

  const mutationError = createState.error || updateState.error || activeState.error;

  return (
    <SettingsPage
      title={S.settings.specialties.title}
      hint={S.settings.common.noDelete}
      toolbar={
        <>
          <ShowInactiveToggle checked={includeInactive} onChange={setIncludeInactive} />
          <Button
            variant="primary"
            onClick={() => setEditing({ mode: 'new', node: null })}
            className="gap-2"
          >
            <Plus size={15} aria-hidden="true" />
            {S.settings.common.newItem}
          </Button>
        </>
      }
    >
      <MutationError error={mutationError} />
      <LoadingOrEmpty loading={loading && rows.length === 0} empty={!loading && rows.length === 0} />

      <ul className="flex flex-col gap-1">
        {roots.map((node) => (
          <TreeNode
            key={node.id}
            node={node}
            byParent={byParent}
            depth={0}
            onEdit={(n) => setEditing({ mode: 'edit', node: n })}
            onAddChild={(n) => setEditing({ mode: 'child', node: n })}
            onToggle={setConfirming}
          />
        ))}
      </ul>

      {editing && (
        <SpecialtyDialog
          mode={editing.mode}
          node={editing.node}
          rows={rows}
          byParent={byParent}
          busy={createState.loading || updateState.loading}
          onClose={() => setEditing(null)}
          onSubmit={async (values) => {
            if (editing.mode === 'edit') {
              await updateLookup({ variables: { input: { id: editing.node.id, ...values } } });
            } else {
              await createLookup({ variables: { input: { kind: KIND, ...values } } });
            }
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirming)}
        onOpenChange={(open) => !open && setConfirming(null)}
        title={confirming?.isActive ? S.app.deactivate : S.app.activate}
        message={
          confirming?.isActive
            ? S.settings.common.confirmDeactivate(confirming?.labelFa ?? '')
            : S.settings.common.confirmActivate(confirming?.labelFa ?? '')
        }
        danger={confirming?.isActive}
        confirmLabel={confirming?.isActive ? S.app.deactivate : S.app.activate}
        onConfirm={() =>
          setActive({ variables: { id: confirming.id, isActive: !confirming.isActive } })
        }
      />
    </SettingsPage>
  );
}

function TreeNode({ node, byParent, depth, onEdit, onAddChild, onToggle }) {
  const children = byParent.get(node.id) ?? [];
  const hasActiveChildren = children.some((c) => c.isActive);

  return (
    <li>
      <div
        className={`flex items-center gap-3 rounded border border-border bg-surface px-4 py-2
                    ${node.isActive ? '' : 'opacity-60'}`}
        // Indentation is structural, not decorative: it encodes tree depth.
        // Uses a spacing token multiple, not a raw pixel value.
        style={{ marginInlineStart: `calc(var(--mh-space-6) * ${depth})` }}
      >
        <span className="font-medium">{node.labelFa}</span>
        <span dir="ltr" className="text-xs text-text-muted">
          {node.code}
        </span>
        <ActiveBadge isActive={node.isActive} />
        <SystemLock isSystem={node.isSystem} />

        <span className="ms-auto flex items-center gap-1">
          <Button variant="ghost" title={S.app.edit} aria-label={S.app.edit} onClick={() => onEdit(node)}>
            <Pencil size={14} aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            title={S.settings.specialties.addChild}
            aria-label={S.settings.specialties.addChild}
            onClick={() => onAddChild(node)}
          >
            <Plus size={14} aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            disabled={(node.isSystem && node.isActive) || (node.isActive && hasActiveChildren)}
            title={
              node.isActive && hasActiveChildren
                ? S.settings.specialties.hasActiveChildren
                : node.isSystem && node.isActive
                  ? S.settings.common.systemLocked
                  : ''
            }
            onClick={() => onToggle(node)}
          >
            {node.isActive ? S.app.deactivate : S.app.activate}
          </Button>
        </span>
      </div>

      {children.length > 0 && (
        <ul className="mt-1 flex flex-col gap-1">
          {children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              byParent={byParent}
              depth={depth + 1}
              onEdit={onEdit}
              onAddChild={onAddChild}
              onToggle={onToggle}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function SpecialtyDialog({ mode, node, rows, byParent, onClose, onSubmit, busy }) {
  const isEdit = mode === 'edit';
  const [code, setCode] = useState(isEdit ? node.code : '');
  const [labelFa, setLabelFa] = useState(isEdit ? node.labelFa : '');
  const [labelEn, setLabelEn] = useState(isEdit ? (node.labelEn ?? '') : '');
  const [parentId, setParentId] = useState(
    mode === 'child' ? node.id : isEdit ? (node.parentId ?? '') : '',
  );

  /**
   * A node may not move under itself or any of its descendants. The server
   * rejects a cycle regardless; excluding them here means the admin never gets
   * to pick an option that is guaranteed to fail.
   */
  const forbidden = useMemo(() => {
    if (!isEdit) return new Set();
    const set = descendantIds(byParent, node.id);
    set.add(node.id);
    return set;
  }, [isEdit, node, byParent]);

  const parentOptions = rows.filter((r) => !forbidden.has(r.id));

  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={
        isEdit
          ? `${S.app.edit} — ${node.labelFa}`
          : mode === 'child'
            ? `${S.settings.specialties.addChild} — ${node.labelFa}`
            : S.settings.lookups.createTitle
      }
      busy={busy}
      onSubmit={() =>
        onSubmit(
          isEdit
            ? { labelFa, labelEn, setParent: true, parentId: parentId || null }
            : { code: code.trim(), labelFa, labelEn, parentId: parentId || null }
        )
      }
    >
      <TextField
        label={S.settings.common.code}
        value={code}
        dir="ltr"
        required={!isEdit}
        disabled={isEdit}
        hint={isEdit ? S.settings.common.codeImmutable : S.settings.lookups.codeHint}
        onChange={(event) => setCode(event.target.value.toUpperCase())}
      />
      <TextField
        label={S.settings.common.labelFa}
        value={labelFa}
        required
        onChange={(event) => setLabelFa(event.target.value)}
      />
      <TextField
        label={S.settings.common.labelEn}
        value={labelEn}
        dir="ltr"
        onChange={(event) => setLabelEn(event.target.value)}
      />
      <SelectField
        label={S.settings.specialties.moveTo}
        value={parentId}
        onChange={(event) => setParentId(event.target.value)}
        hint={isEdit ? S.settings.specialties.cycleRejected : undefined}
      >
        <option value="">{S.settings.specialties.rootLevel}</option>
        {parentOptions.map((option) => (
          <option key={option.id} value={option.id}>
            {option.labelFa}
          </option>
        ))}
      </SelectField>
      {isEdit && (
        <p className="flex items-center gap-1 text-xs text-text-muted">
          <CornerDownLeft size={12} aria-hidden="true" />
          {S.settings.specialties.makeRoot}
        </p>
      )}
    </FormDialog>
  );
}
